import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import MedicalCard from "./MedicalCard";
import PageContainer from "../../../components/container/PageContainer";
import FeatherIcon from "feather-icons-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from "@mui/material";

import {
    GetAllEmployeeDetailsDropdowns,
    GetEmployeeCardDetails,
} from "../../../services/human-resource";

import { GetUserDetail } from "../../../services/user";

const AUTO_ROTATION_SPEED = 28;
const MANUAL_FLIP_DURATION = 700;

const initialData = {
    empName: "",
    empId: "",
    birthDate: "",
    bloodGroup: "",
    familyContact: "",
    empPhotoUrl: "",
    signatureUrl: "/signature.png",
    logoUrl: "/shukla_logo.png",

    ssWhiteContacts: [],

    srsContacts: [
        {
            name: "Dhatriben",
            phone: "72111 44976",
        },
        {
            name: "Dimpalben",
            phone: "72111 44975",
        },
    ],

    emergencyContact: "72111 44978",

    companyNameBack:
        "S.S. White Technologies India Pvt Ltd.",

    companyAddress:
        "Survey No. 2788/3, Rajkot-Ahmedabad Hwy, Near Krishna Petrol Pump, Wadhwan, Gujarat 363030",

    companyTitleFront: "SHUKLA MEDICAL",

    companySubtitleFront:
        "Universal Orthopedic Extraction Technologies",

    companyTitleBack: "S.S. WHITE",
    companySubtitleBack: "Technologies",

    customColorFront: "#002d62",
    customColorBack: "#002d62",

    sSWhiteBackImg:
        "/SS_White_Med_Card_Back_Img.jpg",

    qrCodeUrl: "",
    family: [],
};

const getEmployeePhotoUrl = (picture, personId) => {
    if (picture) {
        return `https://dashboardapi.sswhite.co/SSWhiteImages/${picture}`;
    }

    if (personId) {
        return `https://hrms.sswhite.co/pic/${personId}.png`;
    }

    return "";
};

export default function MedicalCardPage() {
    const [isAutoRotating, setIsAutoRotating] =
        useState(true);

    const [bulkFlipStates, setBulkFlipStates] =
        useState({});

    const [mode, setMode] = useState("single");
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [
        selectedEmployees,
        setSelectedEmployees,
    ] = useState([]);

    const [cardData, setCardData] =
        useState(initialData);

    const [bulkCardData, setBulkCardData] =
        useState([]);

    const [
        employeeSearchTerm,
        setEmployeeSearchTerm,
    ] = useState("");

    const [
        isZebraPrinting,
        setIsZebraPrinting,
    ] = useState(false);

    const frontRef = useRef(null);
    const backRef = useRef(null);
    const wrapperRef = useRef(null);
    const flipperRef = useRef(null);
    const searchInputRef = useRef(null);

    /*
     * Rotation values are stored in refs.
     * Updating these refs does not rerender the complete page.
     */
    const rotationAngleRef = useRef(0);
    const animationFrameRef = useRef(null);
    const lastTimestampRef = useRef(null);

    const applyRotation = useCallback(
        (angle, animate = false) => {
            const flipper = flipperRef.current;

            if (!flipper) {
                return;
            }

            const transition = animate
                ? `transform ${MANUAL_FLIP_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`
                : "none";

            if (
                flipper.style.transition !== transition
            ) {
                flipper.style.transition = transition;
            }

            flipper.style.transform =
                `rotateY(${angle}deg)`;
        },
        []
    );

    const stopRotationFrame = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(
                animationFrameRef.current
            );

            animationFrameRef.current = null;
        }

        lastTimestampRef.current = null;
    }, []);

    const resetSingleRotation = useCallback(() => {
        rotationAngleRef.current = 0;
        lastTimestampRef.current = null;

        applyRotation(0, false);
    }, [applyRotation]);

    /*
     * Initialize page data.
     */
    useEffect(() => {
        const initializePage = async () => {
            await loadEmployees();
            await loadLoggedInUserCard();
        };

        initializePage();
    }, []);

    /*
     * Find the single card flipper after rendering.
     */
    useEffect(() => {
        if (mode !== "single") {
            flipperRef.current = null;
            return undefined;
        }

        const frameId = requestAnimationFrame(() => {
            const flipper =
                wrapperRef.current?.querySelector(
                    ".card-flipper"
                );

            flipperRef.current = flipper || null;

            if (flipper) {
                flipper.style.willChange = "transform";

                applyRotation(
                    rotationAngleRef.current,
                    false
                );
            }
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [
        mode,
        cardData.empId,
        applyRotation,
    ]);

    /*
     * Smooth auto rotation.
     *
     * This directly updates the transform property.
     * It does not call setState on every frame.
     */
    useEffect(() => {
        stopRotationFrame();

        if (
            !isAutoRotating ||
            mode !== "single"
        ) {
            return undefined;
        }

        const animate = (timestamp) => {
            if (
                lastTimestampRef.current === null
            ) {
                lastTimestampRef.current = timestamp;
            }

            const elapsedTime = Math.min(
                timestamp -
                lastTimestampRef.current,
                50
            );

            lastTimestampRef.current = timestamp;

            rotationAngleRef.current +=
                AUTO_ROTATION_SPEED *
                (elapsedTime / 1000);

            /*
             * Avoid extremely large transform values
             * when the page stays open for a long time.
             */
            if (
                rotationAngleRef.current >
                100000
            ) {
                rotationAngleRef.current %= 360;
            }

            applyRotation(
                rotationAngleRef.current,
                false
            );

            animationFrameRef.current =
                requestAnimationFrame(animate);
        };

        animationFrameRef.current =
            requestAnimationFrame(animate);

        return stopRotationFrame;
    }, [
        isAutoRotating,
        mode,
        applyRotation,
        stopRotationFrame,
    ]);

    /*
     * Stop single-card rotation when bulk mode opens.
     */
    useEffect(() => {
        if (mode === "bulk") {
            stopRotationFrame();

            setIsAutoRotating(false);

            rotationAngleRef.current = 0;
            lastTimestampRef.current = null;
            flipperRef.current = null;
        }
    }, [mode, stopRotationFrame]);

    /*
     * Zebra print cleanup.
     */
    useEffect(() => {
        const cleanupZebraPrint = () => {
            document.body.classList.remove(
                "zebra-medical-print"
            );

            setIsZebraPrinting(false);
        };

        window.addEventListener(
            "afterprint",
            cleanupZebraPrint
        );

        return () => {
            window.removeEventListener(
                "afterprint",
                cleanupZebraPrint
            );

            document.body.classList.remove(
                "zebra-medical-print"
            );
        };
    }, []);

    /*
     * Cancel animation when component unmounts.
     */
    useEffect(() => {
        return () => {
            stopRotationFrame();
        };
    }, [stopRotationFrame]);

    const loadLoggedInUserCard = async () => {
        try {
            const userResponse =
                await GetUserDetail();

            const personId =
                userResponse.data.personId;

            const medicalResponse =
                await GetEmployeeCardDetails(
                    personId
                );

            const rows =
                medicalResponse.data
                    ?.employeeDetails || [];

            const coordinators =
                medicalResponse.data
                    ?.medicalCardCoordinators || [];

            const medical = rows[0];

            const family = rows
                .filter(
                    (row) =>
                        row.familyMemberName
                )
                .map(
                    (row) =>
                        row.familyMemberName
                );

            const employeeData = {
                ...initialData,

                empName: [
                    userResponse.data.firstName,
                    userResponse.data.lastName,
                ]
                    .filter(Boolean)
                    .join(" "),

                empId: personId,
                birthDate: medical?.dob,
                bloodGroup:
                    medical?.bloodGroup,

                familyContact:
                    medical?.phoneNo,

                family,

                empPhotoUrl:
                    getEmployeePhotoUrl(
                        medical?.picture,
                        personId
                    ),

                address1: medical?.add1,
                address2: medical?.add2,
                doj: medical?.doj,
                doi: medical?.doi,

                ssWhiteContacts:
                    coordinators.map(
                        (coordinator) => ({
                            name:
                                coordinator.name,
                            phone:
                                coordinator.mobile,
                        })
                    ),

                medicalCard:
                    medical?.medicalCard,
            };

            setSelectedEmployees([
                String(personId),
            ]);

            setCardData(employeeData);
            setBulkCardData([employeeData]);
        } catch (error) {
            console.error(
                "Error loading logged-in employee:",
                error
            );
        }
    };

    const loadEmployees = async () => {
        try {
            setLoading(true);

            const { data } =
                await GetAllEmployeeDetailsDropdowns(
                    true
                );

            const employeeList = (
                data?.buddyDropDown || []
            ).map((employee) => ({
                ...initialData,

                empName:
                    employee.buddyName,

                empId:
                    employee.personID,

                userID:
                    employee.userID,

                personID:
                    employee.personID,

                buddyName:
                    employee.buddyName,
            }));

            setEmployees(employeeList);

            if (employeeList.length) {
                setSelectedEmployees([
                    String(
                        employeeList[0].empId
                    ),
                ]);
            }
        } catch (error) {
            console.error(
                "Error loading employees:",
                error
            );
        } finally {
            setLoading(false);
        }
    };

    const loadSelectedEmployee = async (
        employee
    ) => {
        try {
            const response =
                await GetEmployeeCardDetails(
                    employee.personID
                );

            const rows =
                response.data
                    ?.employeeDetails || [];

            const coordinators =
                response.data
                    ?.medicalCardCoordinators || [];

            const medical = rows[0];

            const family = rows
                .filter(
                    (row) =>
                        row.familyMemberName
                )
                .map(
                    (row) =>
                        row.familyMemberName
                );

            setCardData({
                ...initialData,

                empName: employee.empName,
                empId: employee.personID,

                birthDate: medical?.dob,
                bloodGroup:
                    medical?.bloodGroup,

                familyContact:
                    medical?.phoneNo,

                family,

                empPhotoUrl:
                    getEmployeePhotoUrl(
                        medical?.picture,
                        employee.personID
                    ),

                address1: medical?.add1,
                address2: medical?.add2,
                doj: medical?.doj,
                doi: medical?.doi,

                ssWhiteContacts:
                    coordinators.map(
                        (coordinator) => ({
                            name:
                                coordinator.name,
                            phone:
                                coordinator.mobile,
                        })
                    ),

                medicalCard:
                    medical?.medicalCard,
            });
        } catch (error) {
            console.error(
                "Error loading selected employee:",
                error
            );
        }
    };

    const loadBulkCards = async (
        personIds
    ) => {
        try {
            if (!personIds.length) {
                setBulkCardData([]);
                setBulkFlipStates({});
                return;
            }

            const response =
                await GetEmployeeCardDetails(
                    personIds.join(",")
                );

            const medicalMap = {};

            const employeeRows =
                response.data
                    ?.employeeDetails || [];

            const coordinators =
                response.data
                    ?.medicalCardCoordinators || [];

            employeeRows.forEach((row) => {
                const id = String(
                    row.personID
                );

                if (!medicalMap[id]) {
                    medicalMap[id] = {
                        ...row,
                        family: [],
                    };
                }

                if (row.familyMemberName) {
                    medicalMap[id].family.push(
                        row.familyMemberName
                    );
                }
            });

            const cards = personIds.map(
                (id) => {
                    const employee =
                        employees.find(
                            (item) =>
                                String(
                                    item.personID
                                ) ===
                                String(id)
                        );

                    const detail =
                        medicalMap[
                        String(id)
                        ];

                    return {
                        ...initialData,

                        empName:
                            employee?.empName ||
                            "",

                        empId:
                            employee?.personID ||
                            id,

                        birthDate:
                            detail?.dob,

                        bloodGroup:
                            detail?.bloodGroup,

                        familyContact:
                            detail?.phoneNo,

                        family:
                            detail?.family ||
                            [],

                        empPhotoUrl:
                            getEmployeePhotoUrl(
                                detail?.picture,
                                id
                            ),

                        address1:
                            detail?.add1,

                        address2:
                            detail?.add2,

                        doj: detail?.doj,
                        doi: detail?.doi,

                        ssWhiteContacts:
                            coordinators.map(
                                (
                                    coordinator
                                ) => ({
                                    name:
                                        coordinator.name,

                                    phone:
                                        coordinator.mobile,
                                })
                            ),

                        medicalCard:
                            detail?.medicalCard,
                    };
                }
            );

            setBulkCardData(cards);

            const flipStates = {};

            cards.forEach((card) => {
                flipStates[card.empId] =
                    false;
            });

            setBulkFlipStates(flipStates);
        } catch (error) {
            console.error(
                "Error loading bulk cards:",
                error
            );
        }
    };

    const getFilteredEmployees = () => {
        const searchText =
            employeeSearchTerm
                .trim()
                .toLowerCase();

        if (!searchText) {
            return employees;
        }

        return employees.filter(
            (employee) => {
                const nameMatches =
                    employee.empName
                        ?.toLowerCase()
                        .includes(
                            searchText
                        ) || false;

                const idMatches = String(
                    employee.empId
                ).includes(searchText);

                return (
                    nameMatches ||
                    idMatches
                );
            }
        );
    };

    /*
     * Smooth manual flip.
     *
     * The card continues in the same forward direction
     * instead of jumping back to zero.
     */
    const handleSingleFlip = () => {
        if (mode !== "single") {
            return;
        }

        stopRotationFrame();
        setIsAutoRotating(false);

        const currentAngle =
            rotationAngleRef.current;

        const normalizedAngle =
            ((currentAngle % 360) + 360) %
            360;

        const isBackVisible =
            normalizedAngle >= 90 &&
            normalizedAngle < 270;

        /*
         * If back is visible, rotate to the next front.
         * If front is visible, rotate to the next back.
         */
        const targetFaceAngle =
            isBackVisible ? 0 : 180;

        const completedTurns = Math.ceil(
            (currentAngle -
                targetFaceAngle) /
            360
        );

        let targetAngle =
            targetFaceAngle +
            completedTurns * 360;

        /*
         * Ensure rotation always moves forward.
         */
        if (
            targetAngle <=
            currentAngle + 0.001
        ) {
            targetAngle += 360;
        }

        rotationAngleRef.current =
            targetAngle;

        requestAnimationFrame(() => {
            applyRotation(
                targetAngle,
                true
            );
        });
    };

    const toggleRotation = () => {
        if (mode !== "single") {
            return;
        }

        if (isAutoRotating) {
            stopRotationFrame();
            setIsAutoRotating(false);
            return;
        }

        if (flipperRef.current) {
            flipperRef.current.style.transition =
                "none";
        }

        lastTimestampRef.current = null;
        setIsAutoRotating(true);
    };

    const handleBulkCardFlip = (
        empId
    ) => {
        setBulkFlipStates(
            (previousStates) => ({
                ...previousStates,

                [empId]:
                    !previousStates[empId],
            })
        );
    };

    const handleFlipAll = () => {
        const cardIds =
            bulkCardData.map(
                (card) => card.empId
            );

        const allFlipped =
            cardIds.length > 0 &&
            cardIds.every(
                (id) =>
                    bulkFlipStates[id] ===
                    true
            );

        const newFlipStates = {};

        cardIds.forEach((id) => {
            newFlipStates[id] =
                !allFlipped;
        });

        setBulkFlipStates(
            newFlipStates
        );
    };

    const captureCardElement = async (
        element
    ) => {
        if (!element) {
            throw new Error(
                "Card element is unavailable."
            );
        }

        const oldTransform =
            element.style.transform;

        const oldTransition =
            element.style.transition;

        element.style.transform = "none";
        element.style.transition = "none";

        try {
            const images = [
                ...element.querySelectorAll(
                    "img"
                ),
            ];

            await Promise.all(
                images.map((image) => {
                    if (
                        image.complete &&
                        image.naturalWidth > 0
                    ) {
                        return Promise.resolve();
                    }

                    return new Promise(
                        (resolve) => {
                            const finish = () => {
                                image.removeEventListener(
                                    "load",
                                    finish
                                );

                                image.removeEventListener(
                                    "error",
                                    finish
                                );

                                resolve();
                            };

                            image.addEventListener(
                                "load",
                                finish
                            );

                            image.addEventListener(
                                "error",
                                finish
                            );
                        }
                    );
                })
            );

            return await html2canvas(
                element,
                {
                    scale: 4,
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor:
                        "#ffffff",
                    logging: false,
                    imageTimeout: 0,
                }
            );
        } finally {
            element.style.transform =
                oldTransform;

            element.style.transition =
                oldTransition;
        }
    };

    const exportSinglePDF = async () => {
        try {
            setExporting(true);

            const container =
                wrapperRef.current;

            if (!container) {
                return;
            }

            const frontCard =
                container.querySelector(
                    ".card-front-face"
                );

            const backCard =
                container.querySelector(
                    ".card-back-face"
                );

            if (
                !frontCard ||
                !backCard
            ) {
                return;
            }

            const frontCanvas =
                await captureCardElement(
                    frontCard
                );

            const backCanvas =
                await captureCardElement(
                    backCard
                );

            const pdf = new jsPDF(
                "p",
                "mm",
                "a4"
            );

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 15;

            const cardWidth =
                pageWidth - margin * 2;

            const cardHeight =
                (cardWidth * 340) /
                540;

            const spacing = 10;

            const totalHeight =
                cardHeight * 2 +
                spacing;

            const startY =
                (pageHeight -
                    totalHeight) /
                2;

            pdf.addImage(
                frontCanvas,
                "PNG",
                margin,
                startY,
                cardWidth,
                cardHeight,
                undefined,
                "FAST"
            );

            pdf.addImage(
                backCanvas,
                "PNG",
                margin,
                startY +
                cardHeight +
                spacing,
                cardWidth,
                cardHeight,
                undefined,
                "FAST"
            );

            pdf.save(
                `Medical_Card_${cardData.empName ||
                cardData.empId ||
                "Employee"
                }.pdf`
            );
        } catch (error) {
            console.error(
                "Error exporting PDF:",
                error
            );

            window.alert(
                "Error exporting PDF. Please try again."
            );
        } finally {
            setExporting(false);
        }
    };

    const exportBulkPDF = async () => {
        try {
            setExporting(true);

            const cardElements =
                document.querySelectorAll(
                    ".bulk-card"
                );

            if (!cardElements.length) {
                return;
            }

            const allCards = [];

            for (
                const cardElement of
                cardElements
            ) {
                const frontCard =
                    cardElement.querySelector(
                        ".card-front-face"
                    );

                const backCard =
                    cardElement.querySelector(
                        ".card-back-face"
                    );

                if (
                    frontCard &&
                    backCard
                ) {
                    const frontCanvas =
                        await captureCardElement(
                            frontCard
                        );

                    const backCanvas =
                        await captureCardElement(
                            backCard
                        );

                    allCards.push({
                        front:
                            frontCanvas,
                        back:
                            backCanvas,
                    });
                }
            }

            if (!allCards.length) {
                return;
            }

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: false,
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 15;

            const cardWidth =
                pageWidth -
                margin * 2;

            const cardHeight =
                (cardWidth * 340) /
                540;

            const spacing = 10;

            allCards.forEach(
                (card, index) => {
                    if (index > 0) {
                        pdf.addPage();
                    }

                    const totalHeight =
                        cardHeight * 2 +
                        spacing;

                    const startY =
                        (pageHeight -
                            totalHeight) /
                        2;

                    const frontImage =
                        card.front.toDataURL(
                            "image/jpeg",
                            1
                        );

                    const backImage =
                        card.back.toDataURL(
                            "image/jpeg",
                            1
                        );

                    pdf.addImage(
                        frontImage,
                        "JPEG",
                        margin,
                        startY,
                        cardWidth,
                        cardHeight
                    );

                    pdf.addImage(
                        backImage,
                        "JPEG",
                        margin,
                        startY +
                        cardHeight +
                        spacing,
                        cardWidth,
                        cardHeight
                    );
                }
            );

            pdf.save(
                `Medical_Cards_Bulk_${new Date()
                    .toISOString()
                    .split("T")[0]
                }.pdf`
            );
        } catch (error) {
            console.error(
                "Error exporting bulk PDF:",
                error
            );

            window.alert(
                "Error exporting PDF. Please try again."
            );
        } finally {
            setExporting(false);
        }
    };

    const handleExportPDF = () => {
        if (exporting) {
            return;
        }

        if (mode === "single") {
            exportSinglePDF();
        } else {
            exportBulkPDF();
        }
    };

    const handleZebraPrint = () => {
        document.body.classList.add(
            "zebra-medical-print"
        );

        setIsZebraPrinting(true);

        setTimeout(() => {
            window.print();
        }, 0);
    };

    const inlineStyles = `
        .medical-card-page {
            min-height: calc(100vh - 120px);
            padding: 24px;
            background: linear-gradient(
                135deg,
                #f8fbff 0%,
                #eef6ff 100%
            );
        }

        .medical-card-page__header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            margin-bottom: 24px;
        }

        .medical-card-page__eyebrow {
            color: #2563eb;
            text-transform: uppercase;
            letter-spacing: 0.2em;
            font-size: 0.75rem;
            font-weight: 700;
            margin-bottom: 6px;
        }

        .medical-card-page__header h1 {
            font-size: 1.7rem;
            margin-bottom: 6px;
            color: #0f172a;
        }

        .medical-card-page__header p {
            color: #475569;
            max-width: 720px;
        }

        .medical-card-page__preview {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 20px 0;
            background: transparent;
            border: none;
            border-radius: 0;
            box-shadow: none;
            min-height: 550px;
        }

        .medical-card-page__preview-card {
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .medical-card-page .btn-secondary {
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            padding: 8px 12px;
            background: white;
            color: #0f172a;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
        }

        .medical-card-page .card-scene {
            width: 540px;
            height: 340px;
            perspective: 1700px;
            -webkit-perspective: 1700px;
            perspective-origin: center center;
            -webkit-perspective-origin: center center;
            cursor: pointer;
            isolation: isolate;
        }

        .medical-card-page .card-flipper {
            --card-thickness: 6px;
            --card-half-thickness: 3px;
            --card-negative-half-thickness: -3px;

            width: 100%;
            height: 100%;
            position: relative;
            transform-style: preserve-3d;
            -webkit-transform-style: preserve-3d;
            transform: none;
            transition: none;
            will-change: auto;
        }

        /*
         * Real 3D side edges. These are visible only while the card
         * is turning, so the card looks slightly thick instead of flat.
         */
        .medical-card-page .card-flipper::before,
        .medical-card-page .card-flipper::after {
            content: "";
            position: absolute;
            top: 18px;
            width: var(--card-thickness);
            height: calc(100% - 36px);
            z-index: 1;
            pointer-events: none;
            backface-visibility: visible;
            -webkit-backface-visibility: visible;
            background:
                linear-gradient(
                    90deg,
                    #94a3b8 0%,
                    #e2e8f0 28%,
                    #f8fafc 50%,
                    #cbd5e1 72%,
                    #64748b 100%
                );
            box-shadow:
                inset 0 0 1px rgba(255, 255, 255, 0.85),
                0 0 2px rgba(15, 23, 42, 0.18);
        }

        .medical-card-page .card-flipper::before {
            left: 0;
            transform-origin: left center;
            transform:
                translateZ(var(--card-half-thickness))
                rotateY(90deg);
        }

        .medical-card-page .card-flipper::after {
            right: 0;
            transform-origin: right center;
            transform:
                translateZ(var(--card-negative-half-thickness))
                rotateY(90deg);
        }

        .medical-card-page .card-flipper.flipped {
            transform: rotateY(180deg);
            transition:
                transform 700ms
                cubic-bezier(0.22, 1, 0.36, 1);
        }

        .medical-card-page .medical-card-face {
            position: absolute;
            inset: 0;
            z-index: 2;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            transform-style: preserve-3d;
            -webkit-transform-style: preserve-3d;
            border-radius: 22px;
            overflow: hidden;
            box-shadow:
                0 18px 45px rgba(15, 23, 42, 0.16),
                0 2px 5px rgba(15, 23, 42, 0.12);
        }


        .medical-card-page .medical-card-face {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: geometricPrecision;
        }

        .medical-card-page .medical-card-face img {
            image-rendering: auto;
        }

        .medical-card-page .card-front-face {
            transform: none;
        }

        .medical-card-page .card-back-face {
            transform: rotateY(180deg);
        }

        .medical-card-page .card-bg-geometry {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
        }

        .medical-card-page .card-content {
            position: relative;
            z-index: 2;
            display: flex;
            flex-direction: column;
            height: 100%;
            padding: 20px 24px 18px;
        }

        .medical-card-page .card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
        }

        .medical-card-page .card-logo-container {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .medical-card-page .card-logo-text {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .medical-card-page .card-logo-title {
            font-size: 1rem;
            font-weight: 800;
            letter-spacing: 0.04em;
        }

        .medical-card-page .card-logo-subtitle {
            font-size: 0.62rem;
            color: #64748b;
        }

        .medical-card-page .card-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            gap: 8px;
            padding-left: 4px;
            margin-top: -2px;
        }

        .medical-card-page .employee-name {
            font-size: 1.15rem;
            font-weight: 800;
            color: #0076c0;
            margin-bottom: 2px;
        }

        .medical-card-page .info-grid {
            display: flex;
            flex-direction: column;
            gap: 8px;
            font-size: 0.72rem;
        }

        .medical-card-page .info-row {
            display: grid;
            grid-template-columns: 110px 1fr;
            align-items: center;
        }

        .medical-card-page .info-label {
            font-size: 0.75rem;
            font-weight: 600;
        }

        .medical-card-page .info-value {
            font-size: 0.75rem;
            font-weight: 700;
        }

        .medical-card-page .authorized-sign {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 130px;
            gap: 1px;
            margin-top: 12px;
        }

        .medical-card-page .auth-label {
            font-size: 0.68rem;
            color: #64748b;
            font-weight: 600;
            white-space: nowrap;
        }

        .medical-card-page .auth-signature {
            width: 90px;
            height: 58px;
            margin-top: 15px;
            object-fit: contain;
        }

        .medical-card-page .employee-photo-frame {
            position: absolute;
            right: 35px;
            top: 115px;
            width: 110px;
            height: 110px;
            transform: rotate(50deg);
            overflow: hidden;
            z-index: 3;
            background: transparent;
            border-radius: 0;
            box-shadow: none;
            display: block;
        }

        .medical-card-page .employee-photo {
            width: 130%;
            height: 130%;
            object-fit: cover;
            object-position: center top;
            transform:
                rotate(-48deg)
                translate(0%, -10%);
        }

        .medical-card-page .card-back-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0;
            width: 100%;
            margin-top: 12px;
            justify-content: space-between;
            align-items: start;
            transform: translateX(40px);
        }

        .medical-card-page .card-back-column {
            display: flex;
            flex-direction: column;
        }

        .medical-card-page
        .card-back-column:first-child {
            transform: translateX(30px);
        }

        .medical-card-page
        .card-back-column:last-child {
            transform: translateX(-70px);
        }

        .medical-card-page .column-header {
            background: #002d62;
            color: #fff;
            font-size: 0.62rem;
            font-weight: 700;
            border-radius: 12px;
            padding: 4px 16px;
            width: fit-content;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            transform: translateX(-9px);
        }

        .medical-card-page .contact-entry,
        .medical-card-page
        .emergency-contact-highlight {
            display: flex;
            justify-content: space-between;
            font-size: 0.72rem;
            color: #0f172a;
        }

        .medical-card-page .contact-entry {
            flex-direction: column;
            align-items: center;
            transform: translateX(-77px);
            gap: 1px;
            margin-bottom: 4px;
            line-height: 1.15;
        }

        .medical-card-page
        .emergency-contact-highlight {
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            transform: translateX(-76px);
            gap: 0;
            margin-top: 2px;
            padding: 0;
            line-height: 1;
        }

        .medical-card-page .emergency-label {
            display: block;
            margin: 0;
            padding: 0;
            line-height: 1;
            color: #0066cc;
            font-size: 0.71rem;
            font-weight: 700;
        }

        .medical-card-page .emergency-phone {
            display: block;
            margin: 2px 0 0;
            padding: 0;
            line-height: 1;
            color: #334155;
            font-size: 0.71rem;
            font-weight: 700;
        }

        .medical-card-page .contact-name {
            color: #0066cc;
            font-weight: 600;
            margin-bottom: 2px;
            font-size: 0.71rem;
        }

        .medical-card-page .contact-phone {
            color: #111827;
            font-weight: 700;
            margin-top: 0;
            font-size: 0.71rem;
        }

        .medical-card-page .back-na-tag {
            position: absolute;
            right: 80px;
            bottom: 75px;
            font-size: 0.65rem;
            font-weight: 800;
            color: #0056b3;
        }

        .medical-card-page
        .company-footer-details {
            padding-top: 6px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            margin-top: auto;
            width: 100%;
        }

        .medical-card-page .company-name-bold {
            font-weight: 800;
            font-size: 0.9rem;
            color: #000;
            transform: translateX(50px);
        }

        .medical-card-page .company-address {
            width: 50%;
            max-width: 420px;
            text-align: center;
            margin: 0 auto;
            transform: translateX(50px);
            font-size: 0.55rem;
            font-weight: 700;
            line-height: 1.2;
        }

        .medical-card-page .back-qr-code {
            position: absolute;
            right: 50px;
            bottom: 90px;
            z-index: 3;
        }

        .medical-card-page .back-qr-image {
            width: 120px;
            height: 120px;
            object-fit: contain;
            background: #fff;
            padding: 2px;
            border-radius: 4px;
        }

        .medical-card-page .family-row {
            align-items: flex-start;
        }

        .medical-card-page .family-list {
            font-size: 0.68rem;
            display: flex;
            flex-direction: column;
            gap: 2px;
            text-align: left;
            font-weight: 700;
            color: #111827;
            line-height: 1.2;
        }

        .family-list .MuiTypography-root {
            font-size: 0.8rem;
            line-height: 1.1;
            font-weight: 600;
        }

        .employee-search-input {
            background: white;
            padding: 8px 16px;
            position: sticky;
            top: 0;
            z-index: 1;
            border-bottom:
                1px solid #e0e0e0;
        }

        .employee-search-input
        .MuiInputBase-root {
            background: #f5f5f5;
            border-radius: 8px;
        }

        .employee-search-input
        .MuiInputBase-root:hover {
            background: #eeeeee;
        }

        .employee-search-input
        .MuiInputBase-root.Mui-focused {
            background: white;
        }

        .employee-dropdown-paper {
            max-height: 250px !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
        }

        .employee-dropdown-paper
        .MuiList-root {
            padding-top: 0 !important;
            padding-bottom: 8px !important;
        }

        .bulk-print-root {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            align-content: center;
            width: 100%;
            min-height: 550px;
            gap: 24px;
        }

        .bulk-card {
            width: auto;
            display: flex;
            justify-content: center;
            flex: 0 0 auto;
        }

        @media (max-width: 1180px) {
            .medical-card-page__preview {
                min-height: auto;
            }
        }

        @media (max-width: 1024px) {
            .medical-card-page .card-scene {
                width: 100%;
                max-width: 540px;
                height: auto;
                aspect-ratio: 540 / 340;
            }
        }

        @media (max-width: 768px) {
            .medical-card-page {
                padding: 16px;
            }

            .medical-card-page__header {
                flex-direction: column;
            }
        }

        @media (max-width: 640px) {
            .medical-card-page .form-row,
            .medical-card-page .presets-grid,
            .medical-card-page .card-back-grid {
                grid-template-columns: 1fr;
            }
        }

        @media print {
            body * {
                visibility: hidden;
            }

            .medical-card-page,
            .medical-card-page *,
            .medical-card-page__preview,
            .medical-card-page__preview * {
                visibility: visible;
            }

            .medical-card-page {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                margin: 0;
                padding: 0;
                background: white !important;
            }

            .medical-card-page__header,
            .medical-card-toolbar,
            .medical-card-breadcrumb,
            .btn-secondary {
                display: none !important;
            }

            .medical-card-page__preview {
                position: absolute;
                left: 50%;
                top: 0;
                transform: translateX(-50%);
                background: white !important;
                border: none !important;
                box-shadow: none !important;
                padding: 0 !important;
                min-height: auto !important;
            }

            @page {
                size: auto;
                margin: 10mm;
            }

            body {
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }

            .medical-card-page__preview.print-root {
                display: block !important;
                padding: 0 !important;
                background: white !important;
                box-shadow: none !important;
                border: none !important;
            }

            .card-flipper.flipped {
                transform: none !important;
            }

            .bulk-print-root {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                gap: 0 !important;
            }

            .bulk-card {
                display: block !important;
                width: 700px !important;
                margin:
                    0 0 12px 0 !important;
                page-break-inside:
                    avoid !important;
                break-inside:
                    avoid !important;
                page-break-after:
                    auto !important;
            }

            .card-scene {
                width: 356px !important;
                height: auto !important;
                perspective: none !important;
                overflow: visible !important;
                margin: 0 auto !important;
            }

            .card-flipper {
                display: block !important;
                width: 100% !important;
                height: auto !important;
                gap: 15px !important;
                transform: none !important;
                transition: none !important;
            }

            .card-flipper::before,
            .card-flipper::after {
                display: none !important;
                content: none !important;
            }

            .card-front-face,
            .card-back-face {
                position: relative !important;
                display: block !important;
                inset: auto !important;
                transform: none !important;
                backface-visibility:
                    visible !important;
                margin: 0 !important;
            }

            .card-back-face {
                margin-top: 6px !important;
            }

            .medical-card-face {
                width: 540px !important;
                height: 340px !important;
                transform: none !important;
                transform-origin: top left !important;
                overflow: hidden !important;
                box-shadow: none !important;
            }
        }
    `;

    const zebraPrintStyles = `
        @media print {
            @page {
                size: 85.6mm 54mm;
                margin: 0;
            }

            html,
            body.zebra-medical-print {
                width: 85.6mm !important;
                min-width: 85.6mm !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #fff !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }

            body.zebra-medical-print
            .medical-card-toolbar,
            body.zebra-medical-print
            .medical-card-breadcrumb,
            body.zebra-medical-print
            .medical-card-page__header,
            body.zebra-medical-print
            .btn-secondary {
                display: none !important;
            }

            body.zebra-medical-print
            .print-root,
            body.zebra-medical-print
            .print-root *,
            body.zebra-medical-print
            .bulk-print-root,
            body.zebra-medical-print
            .bulk-print-root *,
            body.zebra-medical-print
            .bulk-card,
            body.zebra-medical-print
            .bulk-card *,
            body.zebra-medical-print
            .card-scene,
            body.zebra-medical-print
            .card-scene *,
            body.zebra-medical-print
            .card-flipper,
            body.zebra-medical-print
            .card-flipper *,
            body.zebra-medical-print
            .card-front-face,
            body.zebra-medical-print
            .card-front-face *,
            body.zebra-medical-print
            .card-back-face,
            body.zebra-medical-print
            .card-back-face * {
                visibility: visible !important;
            }

            body.zebra-medical-print
            .medical-card-page {
                position: static !important;
                width: 85.6mm !important;
                min-height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #fff !important;
            }

            body.zebra-medical-print
            .print-root {
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                display: block !important;
                width: 85.6mm !important;
                min-height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #fff !important;
                border: 0 !important;
                box-shadow: none !important;
                overflow: visible !important;
                transform: none !important;
            }

            body.zebra-medical-print
            .bulk-print-root {
                display: block !important;
                width: 85.6mm !important;
                min-height: 0 !important;
                gap: 0 !important;
            }

            body.zebra-medical-print
            .bulk-card {
                display: block !important;
                width: 85.6mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
            }

            body.zebra-medical-print
            .card-scene {
                display: block !important;
                width: 85.6mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                perspective: none !important;
                overflow: visible !important;
                cursor: default !important;
            }

            body.zebra-medical-print
            .card-flipper,
            body.zebra-medical-print
            .card-flipper.flipped {
                position: static !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                width: 85.6mm !important;
                height: auto !important;
                margin: 0 !important;
                padding: 0 !important;
                gap: 0 !important;
                transform: none !important;
                transition: none !important;
                transform-style: flat !important;
            }

            body.zebra-medical-print
            .card-front-face,
            body.zebra-medical-print
            .card-back-face {
                position: relative !important;
                display: block !important;
                inset: auto !important;
                width: 540px !important;
                height: 340px !important;
                min-width: 540px !important;
                min-height: 340px !important;
                margin: 0 auto !important;
                padding: 0 !important;
                overflow: hidden !important;
                box-shadow: none !important;
                backface-visibility:
                    visible !important;
                transform: none !important;
                transform-origin:
                    top left !important;
                page-break-inside:
                    avoid !important;
                break-inside:
                    avoid !important;
            }

            body.zebra-medical-print
            .card-front-face {
                page-break-after:
                    always !important;
                break-after:
                    page !important;
            }

            body.zebra-medical-print
            .card-back-face {
                page-break-after:
                    always !important;
                break-after:
                    page !important;
            }

            body.zebra-medical-print
            .card-back-face
            .card-content {
                display: flex !important;
                visibility: visible !important;
                opacity: 1 !important;
            }

            body.zebra-medical-print
            .card-back-face
            .card-back-grid {
                display: grid !important;
                grid-template-columns:
                    1fr 1fr !important;
                gap: 0 !important;
                width: 100% !important;
                margin-top: 12px !important;
                justify-content:
                    space-between !important;
                align-items:
                    start !important;
                visibility: visible !important;
                opacity: 1 !important;
            }

            body.zebra-medical-print
            .card-back-face
            .card-back-column {
                display: flex !important;
                flex-direction:
                    column !important;
                visibility:
                    visible !important;
            }

            body.zebra-medical-print
            .card-back-face
            .contact-entry,
            body.zebra-medical-print
            .card-back-face
            .company-footer-details {
                visibility:
                    visible !important;
                opacity: 1 !important;
            }

            body.zebra-medical-print
            .medical-card-page__preview
            .card-back-face,
            body.zebra-medical-print
            .bulk-card:last-child
            .card-back-face {
                page-break-after:
                    auto !important;
                break-after:
                    auto !important;
            }
        }
    `;

    const disableActions =
        mode === "single" &&
        isAutoRotating;

    const allBulkCardsFlipped =
        bulkCardData.length > 0 &&
        bulkCardData.every(
            (card) =>
                bulkFlipStates[
                card.empId
                ] === true
        );

    return (
        <Box className="medical-card-page">
            <style>{inlineStyles}</style>

            {isZebraPrinting && (
                <style>
                    {zebraPrintStyles}
                </style>
            )}

            <PageContainer title="Medical Card">
                <Card
                    sx={{
                        p: 0,
                        borderRadius: "20px",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        className="medical-card-toolbar"
                        justifyContent="space-between"
                        p={2}
                        sx={{
                            backgroundColor:
                                "primary.light",

                            color:
                                "primary.main",

                            borderTopLeftRadius:
                                "20px",

                            borderTopRightRadius:
                                "20px",

                            borderBottom:
                                "1px solid #e6eef5",

                            gap: 2,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <FeatherIcon
                                icon="credit-card"
                                width="18"
                            />

                            <Box
                                sx={{
                                    ml: 1,
                                    fontWeight: 600,
                                }}
                            >
                                Medical Card
                            </Box>
                        </Box>

                        <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            flexWrap="wrap"
                        >
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={mode}
                                color="primary"
                                onChange={async (
                                    _,
                                    value
                                ) => {
                                    if (!value) {
                                        return;
                                    }

                                    setEmployeeSearchTerm(
                                        ""
                                    );

                                    if (
                                        value ===
                                        "single"
                                    ) {
                                        rotationAngleRef.current =
                                            0;

                                        lastTimestampRef.current =
                                            null;

                                        setMode(
                                            "single"
                                        );

                                        setIsAutoRotating(
                                            true
                                        );

                                        await loadLoggedInUserCard();
                                    } else {
                                        stopRotationFrame();

                                        setMode(
                                            "bulk"
                                        );

                                        setSelectedEmployees(
                                            [
                                                String(
                                                    cardData.empId
                                                ),
                                            ]
                                        );

                                        setBulkCardData(
                                            [
                                                cardData,
                                            ]
                                        );

                                        setBulkFlipStates(
                                            {
                                                [cardData.empId]:
                                                    false,
                                            }
                                        );

                                        setIsAutoRotating(
                                            false
                                        );

                                        rotationAngleRef.current =
                                            0;
                                    }
                                }}
                                sx={{
                                    height: 40,
                                    backgroundColor:
                                        "#f0f7ff",

                                    borderRadius:
                                        "30px",

                                    padding:
                                        "3px",

                                    border:
                                        "1px solid #dbeafe",

                                    "& .MuiToggleButton-root":
                                    {
                                        px: 3,
                                        py: 1,

                                        textTransform:
                                            "none",

                                        fontWeight:
                                            600,

                                        fontSize:
                                            "0.8rem",

                                        borderRadius:
                                            "30px !important",

                                        border:
                                            "none",

                                        color:
                                            "#94a3b8",

                                        transition:
                                            "all 0.3s ease",

                                        "&:hover":
                                        {
                                            backgroundColor:
                                                "transparent",

                                            color:
                                                "primary.main",
                                        },

                                        "&.Mui-selected":
                                        {
                                            backgroundColor:
                                                "primary.main",

                                            color:
                                                "#ffffff",

                                            "&:hover":
                                            {
                                                backgroundColor:
                                                    "primary.main",
                                            },
                                        },

                                        "&.Mui-selected + &":
                                        {
                                            borderLeft:
                                                "none",
                                        },

                                        "&:not(:first-of-type)":
                                        {
                                            borderLeft:
                                                "none",
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="single">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <FeatherIcon
                                            icon="user"
                                            size={14}
                                            style={{
                                                opacity:
                                                    mode ===
                                                        "single"
                                                        ? 1
                                                        : 0.6,
                                            }}
                                        />

                                        Single
                                    </Box>
                                </ToggleButton>

                                <ToggleButton value="bulk">
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <FeatherIcon
                                            icon="users"
                                            size={14}
                                            style={{
                                                opacity:
                                                    mode ===
                                                        "bulk"
                                                        ? 1
                                                        : 0.6,
                                            }}
                                        />

                                        Bulk
                                    </Box>
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <FormControl
                                size="small"
                                sx={{
                                    width: 300,
                                    minWidth: 300,
                                    maxWidth: 300,
                                    flexShrink: 0,
                                }}
                            >
                                <InputLabel>
                                    Employees
                                </InputLabel>

                                {mode ===
                                    "single" ? (
                                    <Select
                                        value={
                                            selectedEmployees[0] ||
                                            ""
                                        }
                                        label="Employees"
                                        disabled={
                                            loading
                                        }
                                        onChange={async (
                                            event
                                        ) => {
                                            const personId =
                                                event
                                                    .target
                                                    .value;

                                            setSelectedEmployees(
                                                [
                                                    personId,
                                                ]
                                            );

                                            setEmployeeSearchTerm(
                                                ""
                                            );

                                            resetSingleRotation();

                                            const employee =
                                                employees.find(
                                                    (
                                                        item
                                                    ) =>
                                                        String(
                                                            item.personID
                                                        ) ===
                                                        String(
                                                            personId
                                                        )
                                                );

                                            if (
                                                employee
                                            ) {
                                                await loadSelectedEmployee(
                                                    employee
                                                );
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps:
                                            {
                                                className:
                                                    "employee-dropdown-paper",

                                                sx: {
                                                    maxHeight:
                                                        "250px !important",

                                                    overflowY:
                                                        "auto !important",

                                                    overflowX:
                                                        "hidden !important",
                                                },
                                            },

                                            disablePortal:
                                                false,

                                            keepMounted:
                                                true,
                                        }}
                                        renderValue={(
                                            selected
                                        ) => {
                                            const employee =
                                                employees.find(
                                                    (
                                                        item
                                                    ) =>
                                                        String(
                                                            item.personID
                                                        ) ===
                                                        String(
                                                            selected
                                                        )
                                                );

                                            return employee
                                                ? `${employee.empName} (${employee.empId})`
                                                : "";
                                        }}
                                    >
                                        <ListSubheader
                                            className="employee-search-input"
                                            sx={{
                                                pointerEvents:
                                                    "auto",
                                            }}
                                        >
                                            <TextField
                                                inputRef={
                                                    searchInputRef
                                                }
                                                size="small"
                                                placeholder="Search by name or ID..."
                                                value={
                                                    employeeSearchTerm
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setEmployeeSearchTerm(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                onClick={(
                                                    event
                                                ) => {
                                                    event.stopPropagation();
                                                }}
                                                onKeyDown={(
                                                    event
                                                ) => {
                                                    event.stopPropagation();
                                                }}
                                                fullWidth
                                                autoFocus
                                            />
                                        </ListSubheader>

                                        {getFilteredEmployees()
                                            .length ===
                                            0 ? (
                                            <MenuItem
                                                disabled
                                                sx={{
                                                    py: 2,
                                                }}
                                            >
                                                No employees
                                                found
                                            </MenuItem>
                                        ) : (
                                            getFilteredEmployees().map(
                                                (
                                                    employee
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            employee.userID ||
                                                            employee.empId
                                                        }
                                                        value={String(
                                                            employee.empId
                                                        )}
                                                        sx={{
                                                            py: 1,
                                                        }}
                                                    >
                                                        {
                                                            employee.empName
                                                        }{" "}
                                                        (
                                                        {
                                                            employee.empId
                                                        }
                                                        )
                                                    </MenuItem>
                                                )
                                            )
                                        )}
                                    </Select>
                                ) : (
                                    <Select
                                        multiple
                                        value={
                                            selectedEmployees
                                        }
                                        label="Employees"
                                        disabled={
                                            loading
                                        }
                                        onChange={async (
                                            event
                                        ) => {
                                            const ids =
                                                typeof event
                                                    .target
                                                    .value ===
                                                    "string"
                                                    ? event.target.value.split(
                                                        ","
                                                    )
                                                    : event
                                                        .target
                                                        .value;

                                            setSelectedEmployees(
                                                ids
                                            );

                                            setEmployeeSearchTerm(
                                                ""
                                            );

                                            await loadBulkCards(
                                                ids
                                            );
                                        }}
                                        renderValue={(
                                            selected
                                        ) =>
                                            employees
                                                .filter(
                                                    (
                                                        employee
                                                    ) =>
                                                        selected.includes(
                                                            String(
                                                                employee.empId
                                                            )
                                                        )
                                                )
                                                .map(
                                                    (
                                                        employee
                                                    ) =>
                                                        employee.empName
                                                )
                                                .join(
                                                    ", "
                                                )
                                        }
                                        MenuProps={{
                                            PaperProps:
                                            {
                                                className:
                                                    "employee-dropdown-paper",
                                            },

                                            disablePortal:
                                                false,

                                            keepMounted:
                                                true,
                                        }}
                                    >
                                        <ListSubheader
                                            className="employee-search-input"
                                            sx={{
                                                pointerEvents:
                                                    "auto",
                                            }}
                                        >
                                            <TextField
                                                inputRef={
                                                    searchInputRef
                                                }
                                                size="small"
                                                placeholder="Search by name or ID..."
                                                value={
                                                    employeeSearchTerm
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setEmployeeSearchTerm(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                onClick={(
                                                    event
                                                ) => {
                                                    event.stopPropagation();
                                                }}
                                                onKeyDown={(
                                                    event
                                                ) => {
                                                    event.stopPropagation();
                                                }}
                                                fullWidth
                                                autoFocus
                                            />
                                        </ListSubheader>

                                        {getFilteredEmployees()
                                            .length ===
                                            0 ? (
                                            <MenuItem
                                                disabled
                                                sx={{
                                                    py: 2,
                                                }}
                                            >
                                                No employees
                                                found
                                            </MenuItem>
                                        ) : (
                                            getFilteredEmployees().map(
                                                (
                                                    employee
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            employee.userID ||
                                                            employee.empId
                                                        }
                                                        value={String(
                                                            employee.empId
                                                        )}
                                                        sx={{
                                                            py: 1,
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={selectedEmployees.includes(
                                                                String(
                                                                    employee.empId
                                                                )
                                                            )}
                                                        />

                                                        <ListItemText
                                                            primary={`${employee.empName} (${employee.empId})`}
                                                        />
                                                    </MenuItem>
                                                )
                                            )
                                        )}
                                    </Select>
                                )}
                            </FormControl>

                            {mode ===
                                "bulk" &&
                                bulkCardData.length >
                                0 && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={
                                            handleFlipAll
                                        }
                                    >
                                        {allBulkCardsFlipped
                                            ? "Flip to Front"
                                            : "Flip All Cards"}
                                    </Button>
                                )}

                            <Tooltip
                                title={
                                    disableActions
                                        ? "Stop rotation to export"
                                        : ""
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={
                                            handleExportPDF
                                        }
                                        disabled={
                                            exporting ||
                                            disableActions
                                        }
                                        startIcon={
                                            exporting ? (
                                                <CircularProgress
                                                    size={
                                                        16
                                                    }
                                                    color="inherit"
                                                />
                                            ) : (
                                                <FeatherIcon
                                                    icon="file-text"
                                                    size={
                                                        16
                                                    }
                                                />
                                            )
                                        }
                                    >
                                        {exporting
                                            ? "Exporting..."
                                            : "Export to PDF"}
                                    </Button>
                                </span>
                            </Tooltip>

                            <Tooltip
                                title={
                                    disableActions
                                        ? "Stop rotation to print"
                                        : ""
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        disabled={
                                            disableActions
                                        }
                                        onClick={() =>
                                            window.print()
                                        }
                                    >
                                        {mode ===
                                            "bulk"
                                            ? "Bulk Print"
                                            : "Print Card"}
                                    </Button>
                                </span>
                            </Tooltip>

                            <Tooltip
                                title={
                                    disableActions
                                        ? "Stop rotation to print"
                                        : "Use only for Zebra ZXP Series 7"
                                }
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        size="small"
                                        disabled={
                                            disableActions
                                        }
                                        onClick={
                                            handleZebraPrint
                                        }
                                    >
                                        Zebra Print
                                    </Button>
                                </span>
                            </Tooltip>

                            {mode ===
                                "single" && (
                                    <>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={
                                                handleSingleFlip
                                            }
                                        >
                                            Flip Card
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={
                                                toggleRotation
                                            }
                                            sx={{
                                                minWidth:
                                                    "120px",
                                            }}
                                        >
                                            {isAutoRotating
                                                ? "Stop Rotation"
                                                : "Start Rotation"}
                                        </Button>
                                    </>
                                )}
                        </Box>
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                        {mode === "single" ? (
                            <Box
                                className="medical-card-page__preview print-root"
                                ref={wrapperRef}
                            >
                                <MedicalCard
                                    data={
                                        cardData
                                    }
                                    isFlipped={
                                        false
                                    }
                                    frontRef={
                                        frontRef
                                    }
                                    backRef={
                                        backRef
                                    }
                                    onFlip={
                                        handleSingleFlip
                                    }
                                />
                            </Box>
                        ) : (
                            <Box className="bulk-print-root print-root">
                                {bulkCardData.map(
                                    (
                                        employee
                                    ) => (
                                        <Box
                                            key={
                                                employee.empId
                                            }
                                            className="bulk-card"
                                        >
                                            <MedicalCard
                                                data={
                                                    employee
                                                }
                                                isFlipped={
                                                    bulkFlipStates[
                                                    employee
                                                        .empId
                                                    ] ||
                                                    false
                                                }
                                                onFlip={() =>
                                                    handleBulkCardFlip(
                                                        employee.empId
                                                    )
                                                }
                                            />
                                        </Box>
                                    )
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </PageContainer>
        </Box>
    );
}